import type {
  ActionRun,
  ActionRunsListResponse,
} from '@logspot-platform/platform-types';
import { api } from './api-client';

type SuccessEnvelope<T> = { data: T };

const RUN_POLL_MS = 3_000;
const RUN_MAX_POLL_FAILURES = 5;
// A run killed mid-flight stays "running" until the server reaps it, so stop
// waiting well before that and let the caller say it is still running.
const RUN_MAX_WAIT_MS = 5 * 60_000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export type { ActionRun } from '@logspot-platform/platform-types';

// The action-run feed (Activity) + per-run detail. One record per execution.
// Mirrors the backend admin endpoints (api/admin/action-runs.ts).
export const ActionRunsService = {
  list: async (
    params: {
      limit?: number;
      cursor?: string | null;
      actionKey?: string;
    } = {},
  ): Promise<ActionRunsListResponse> => {
    const query = new URLSearchParams();
    if (params.limit != null) query.set('limit', String(params.limit));
    if (params.cursor) query.set('cursor', params.cursor);
    if (params.actionKey) query.set('actionKey', params.actionKey);
    const qs = query.toString();
    const r = await api.get<SuccessEnvelope<ActionRunsListResponse>>(
      `/admin/action-runs${qs ? `?${qs}` : ''}`,
    );
    return r.data;
  },

  get: async (id: string): Promise<ActionRun> => {
    const r = await api.get<SuccessEnvelope<{ run: ActionRun }>>(
      `/admin/action-runs/${id}`,
    );
    return r.data.run;
  },

  // Polls a background run until it finishes. A few dropped polls (flaky mobile
  // network) don't end a run that is still going.
  waitFor: async (
    id: string,
    stillRunningMessage: string,
    maxWaitMs = RUN_MAX_WAIT_MS,
  ): Promise<ActionRun> => {
    let failures = 0;
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      await sleep(RUN_POLL_MS);
      try {
        const run = await ActionRunsService.get(id);
        failures = 0;
        if (run.status !== 'running') return run;
      } catch {
        // The run keeps going server-side; say so rather than inviting a re-run.
        failures += 1;
        if (failures >= RUN_MAX_POLL_FAILURES) break;
      }
    }
    throw new Error(stillRunningMessage);
  },
};
